import { useCallback, useEffect, useState } from 'react';

/**
 * Thông báo góc màn hình MÁY TÍNH (Windows: popup góc phải dưới; macOS: góc
 * phải trên) qua Notification API của trình duyệt — hiện cả khi tab admin đang
 * ở nền hoặc cửa sổ trình duyệt thu nhỏ. Trình duyệt phải còn mở trang admin.
 *
 * Quyền do TRÌNH DUYỆT giữ theo từng trang web (xin một lần, bấm nút). Công tắc
 * bật/tắt riêng của người trực lưu ở localStorage (máy này, trình duyệt này).
 */
const PREF_KEY = 'support.desktopNotify';

export type DesktopPermission = 'unsupported' | 'default' | 'granted' | 'denied';

export function desktopPermission(): DesktopPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as DesktopPermission;
}

export function readDesktopPref(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) !== '0';
  } catch {
    return true;
  }
}

function writeDesktopPref(on: boolean) {
  try {
    localStorage.setItem(PREF_KEY, on ? '1' : '0');
  } catch {
    // bỏ qua
  }
  window.dispatchEvent(new Event('support-desktop-pref'));
}

/**
 * Báo những tin nào: 'all' = mọi tin mới (mặc định — đội nhỏ, ai trực cũng cần biết);
 * 'mine' = chỉ luồng chưa ai nhận hoặc đang giao cho mình (đội đông người).
 */
export type NotifyScope = 'all' | 'mine';
const SCOPE_KEY = 'support.notifyScope';

export function readNotifyScope(): NotifyScope {
  try {
    return localStorage.getItem(SCOPE_KEY) === 'mine' ? 'mine' : 'all';
  } catch {
    return 'all';
  }
}

export function writeNotifyScope(scope: NotifyScope) {
  try {
    localStorage.setItem(SCOPE_KEY, scope);
  } catch {
    // bỏ qua
  }
  window.dispatchEvent(new Event('support-desktop-pref'));
}

/** Đang bật thật sự: trình duyệt cho phép + người trực không tắt. */
export function desktopNotifyActive(): boolean {
  return desktopPermission() === 'granted' && readDesktopPref();
}

export function useDesktopNotify() {
  const [permission, setPermission] = useState<DesktopPermission>(desktopPermission);
  const [pref, setPref] = useState(readDesktopPref);
  const [scope, setScopeState] = useState<NotifyScope>(readNotifyScope);

  useEffect(() => {
    const sync = () => {
      setPermission(desktopPermission());
      setPref(readDesktopPref());
      setScopeState(readNotifyScope());
    };
    window.addEventListener('support-desktop-pref', sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('support-desktop-pref', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  /** Gọi từ một cú bấm (trình duyệt chỉ cho xin quyền khi người dùng bấm). */
  const enable = useCallback(async (): Promise<DesktopPermission> => {
    let next = desktopPermission();
    if (next === 'default') {
      try {
        next = (await Notification.requestPermission()) as DesktopPermission;
      } catch {
        next = desktopPermission();
      }
    }
    setPermission(next);
    if (next === 'granted') {
      writeDesktopPref(true);
      setPref(true);
    }
    return next;
  }, []);

  const disable = useCallback(() => {
    writeDesktopPref(false);
    setPref(false);
  }, []);

  const setScope = useCallback((next: NotifyScope) => {
    writeNotifyScope(next);
    setScopeState(next);
  }, []);

  return { permission, enabled: permission === 'granted' && pref, enable, disable, scope, setScope };
}

export interface DesktopNotice {
  title: string;
  body: string;
  /** Gộp/thay popup cùng một cuộc trò chuyện thay vì xếp chồng. */
  tag: string;
  silent: boolean;
  onClick: () => void;
}

/** Logo HP bên trái popup (public/hp-logo.png). */
export const HP_LOGO_URL = `${process.env.PUBLIC_URL || ''}/hp-logo.png`;

export function showDesktopNotice(notice: DesktopNotice): boolean {
  if (!desktopNotifyActive()) return false;
  try {
    const n = new Notification(notice.title, {
      body: notice.body,
      icon: new URL(HP_LOGO_URL, window.location.origin).toString(),
      badge: new URL(HP_LOGO_URL, window.location.origin).toString(),
      tag: notice.tag,
      // renotify: popup cùng tag vẫn hiện lại + kêu lại khi có tin mới.
      renotify: true,
      silent: notice.silent,
    } as NotificationOptions);
    n.onclick = () => {
      window.focus();
      notice.onClick();
      n.close();
    };
    return true;
  } catch {
    // Một số trình duyệt (Android Chrome) chỉ cho tạo qua service worker.
    return false;
  }
}
